// hooks/useSearch.ts — 1.24.00 real search: debounced server query with an
// in-flight sequence guard.
//
//   • 300 ms debounce — a keystroke burst becomes ONE request, and the request
//     is issued for the LAST value typed, not the first.
//   • Sequence guard — every issued request takes a ticket; only the response
//     for the newest ticket may land. Without it, "abc" (slow) can resolve after
//     "abcd" (fast) and overwrite the fresher results with stale ones.
//   • Under MIN_QUERY_LEN characters the hook is idle: results null, no request,
//     no loading flag — the page keeps its browsing view for that state.
//
// Components must NOT import searchService directly — use this hook.

import { useEffect, useRef, useState } from 'react';
import { searchService } from '@/core/services/searchService';
import type { SearchResults, SearchType } from '@/core/models/search';

export const SEARCH_MIN_QUERY_LEN = 2;
export const SEARCH_DEBOUNCE_MS = 300;

export interface SearchHook {
  /** True from the first keystroke over the threshold until the newest response lands. */
  loading: boolean;
  /** A real failure (400 validation, 429, network) — never "no results". */
  error: string | null;
  /** Null while idle (query too short) or before the first response. */
  results: SearchResults | null;
  /** True when `q` is long enough to search — the page switches views on this, not on results. */
  active: boolean;
}

export function useSearch(q: string, type: SearchType = 'all', limit = 20, filters = ''): SearchHook {
  const trimmed = q.trim();
  const active = trimmed.length >= SEARCH_MIN_QUERY_LEN || filters.length > 0;
  const [state, setState] = useState<{
    loading: boolean;
    error: string | null;
    results: SearchResults | null;
  }>({ loading: false, error: null, results: null });
  // Ticket counter for the sequence guard. A ref, not state: bumping it must
  // not itself cause a render, and the check happens inside an async callback.
  const seq = useRef(0);

  useEffect(() => {
    if (!active) {
      // Invalidate anything in flight so a late response for the previous
      // (longer) query cannot repopulate results after the box was cleared.
      seq.current += 1;
      const t = setTimeout(() => setState({ loading: false, error: null, results: null }), 0);
      return () => clearTimeout(t);
    }
    const ticket = ++seq.current;
    // Loading is flagged immediately (before the debounce elapses) so the UI
    // can show "Searching…" for the whole keystroke-to-response window.
    const flag = setTimeout(() => setState((s) => ({ ...s, loading: true, error: null })), 0);
    const timer = setTimeout(() => {
      searchService.search(trimmed, { type, limit, ...(filters ? { filters } : {}) }).then(
        (results) => {
          if (ticket !== seq.current) return; // stale — a newer query took over
          setState({ loading: false, error: null, results });
        },
        () => {
          if (ticket !== seq.current) return;
          setState({ loading: false, error: "Search isn't available right now. Try again.", results: null });
        },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(flag);
      clearTimeout(timer);
    };
  }, [trimmed, active, type, limit, filters]);

  return { ...state, active };
}
